import { inject, Injectable } from '@angular/core';
import * as R from 'ramda';
import { Observable, of } from 'rxjs';
import { concatAll, first, map, toArray } from 'rxjs/operators';

import { APIService, KPI } from '../../@core/api';
import { idMapToArray, ScopeService } from './scope.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class InvestmentNotationsService {
  private translate = inject(TranslateService);

  constructor(
    private api: APIService,
    private scope: ScopeService,
  ) {
  }

  private readonly _STATE_LABELS = {
    0: this.translate.instant('Dead'),
    22: this.translate.instant('Critical'),
    46: this.translate.instant('Bad'),
    70: this.translate.instant('Acceptable'),
    94: this.translate.instant('Good'),
    100: this.translate.instant('New'),
  };

  // list of available state notations
  STATE_LABELS = Object.entries(this._STATE_LABELS)
    .map(([key, label]) => ({ key: Number(key), label }))
    .sort((a, b) => b.key - a.key);

  labelFromState(state: 0 | 22 | 46 | 70 | 94 | 100): string {
    return this._STATE_LABELS[state];
  }

  /**
   * Returns an Observable emitting the investment notations for the specified investments.
   *
   * @param investments A list of investments.
   */
  public loadInvestmentNotationsByInvestments(
    investments: KPI.Investment[],
  ): Observable<KPI.MappedInvestmentNotation[]> {
    if (investments.length > 0) {
      return this.api.investments_investment_notations(
        {
          investments: R.pluck('id', investments).join(','),
        },
        true,
      ).pipe(
        concatAll(),
        map((investmentNotation) => this.mapInvestmentNotation(investmentNotation)),
        concatAll(),
        toArray(),
      );
    }

    return of([]);
  }

  /**
   * Calls the api to patch the investmentnotation and returns an observer containing the updated notations
   *
   * @param id
   * @param investmentNotation
   */
  public updateInvestmentNotation(
    id: number,
    investmentNotation: Partial<KPI.InvestmentNotation>,
  ): Observable<KPI.MappedInvestmentNotation> {
    return this.api.investments_investment_notations_patch(id, investmentNotation).pipe(
      map(updatedInvestmentNotation => this.mapInvestmentNotation(updatedInvestmentNotation)),
      concatAll(),
    );
  }

  private mapInvestmentNotation(investmentNotation: KPI.InvestmentNotation): Observable<KPI.MappedInvestmentNotation> {
    return new Observable((observer) => {
      this.scope.auditQuestions$
        .pipe(
          first(),
          idMapToArray(),
        )
        .subscribe((auditQuestions) => {
          let auditQuestion: KPI.AuditQuestion;
          let auditQuestionItem: KPI.AuditQuestionItem;
          for (auditQuestion of auditQuestions) {
            auditQuestionItem = auditQuestion.items.find(
              (item) => item.id === investmentNotation.question_item,
            );
            if (auditQuestionItem) {
              break;
            }
          }

          observer.next({
            ...investmentNotation,
            audit_question_object: auditQuestion,
            audit_question_item_object: auditQuestionItem,
          });
          observer.complete();
        });
      })
    ;
  }
}
